import { useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export interface ThemeState {
  mode: ThemeMode;
  /**
   * OKLCH hue 0–360 for the accent colour this browser chose, or `null` to follow the server's
   * branding (context STORY-0170) and, without one, the product's blue.
   */
  accentHue: number | null;
}

const KEY = 'mailserver.theme';
export const PRODUCT_ACCENT_HUE = 250;
const DEFAULT: ThemeState = { mode: 'system', accentHue: null };
const listeners = new Set<() => void>();
let state: ThemeState = load();
/** The server's accent (`GET /branding`), applied when this browser has not chosen one. */
let serverAccentHue: number | null = null;

function load(): ThemeState {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as Partial<ThemeState>;
    return {
      mode: parsed.mode === 'light' || parsed.mode === 'dark' ? parsed.mode : 'system',
      // A stored 250 predates server branding, when every browser saved the product's blue whether or
      // not anybody picked it; read it as "no choice" so a server's accent can show through.
      accentHue:
        typeof parsed.accentHue === 'number' && parsed.accentHue !== PRODUCT_ACCENT_HUE
          ? ((parsed.accentHue % 360) + 360) % 360
          : null,
    };
  } catch {
    return DEFAULT;
  }
}

export function resolveDark(mode: ThemeMode, prefersDark: boolean): boolean {
  return mode === 'dark' || (mode === 'system' && prefersDark);
}

/** The hue actually shown: this browser's pick, else the server's, else the product's. */
export function effectiveAccentHue(): number {
  return state.accentHue ?? serverAccentHue ?? PRODUCT_ACCENT_HUE;
}

/** Writes the current state onto <html>: `.dark` class + `--accent-hue`. Idempotent. */
export function applyTheme(doc: Document = document): void {
  const prefersDark = globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  doc.documentElement.classList.toggle('dark', resolveDark(state.mode, prefersDark));
  doc.documentElement.style.setProperty('--accent-hue', String(effectiveAccentHue()));
}

export function setTheme(patch: Partial<ThemeState>): void {
  state = { ...state, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* private mode etc. — theme just won't persist */
  }
  applyTheme();
  listeners.forEach((l) => l());
}

/** Set by the branding query; never persisted, because the server is asked again on every load. */
export function setServerAccent(hue: number | null): void {
  if (serverAccentHue === hue) return;
  serverAccentHue = hue;
  applyTheme();
  listeners.forEach((l) => l());
}

export function getTheme(): ThemeState {
  return state;
}

export function useTheme(): ThemeState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getTheme,
    getTheme,
  );
}

/**
 * Whether the page is drawn dark now: the chosen mode, or the system's when it is `system`, followed as the
 * system changes. For what the page's own classes cannot reach, such as a sandboxed message frame
 * (context STORY-0287).
 */
export function useDark(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
      media?.addEventListener('change', l);
      return () => {
        listeners.delete(l);
        media?.removeEventListener('change', l);
      };
    },
    () => resolveDark(state.mode, globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false),
    () => false,
  );
}

export function initTheme(): void {
  applyTheme();
  globalThis.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => applyTheme());
}
